/* ==========================================================================
   ODINS — Cart drawer additions
   assets/odins-cart.js

   Loaded once from theme.liquid. Everything works through listeners on
   document and a MutationObserver, because Dawn refreshes the drawer by
   assigning innerHTML — which discards any listener bound to an element
   inside it, and never executes a script tag that arrives that way.
   ========================================================================== */

(function () {
  'use strict';

  var ATTRIBUTE = 'Ring size verified';

  /* ======================================================================
     Made to order date

     Written here rather than in Liquid. `now` in Liquid is captured in
     Shopify's page cache, so a cached cart would show a date computed
     whenever the page was last rendered.
     ====================================================================== */
  function formatDate(days) {
    var d = new Date();
    d.setDate(d.getDate() + days);
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      }).format(d);
    } catch (err) {
      return d.toDateString().slice(0, 10);
    }
  }

  /* Any element carrying a day count gets the date written into it. A
     mixed cart shows two, so this no longer assumes one per block. */
  function paintDates(scope) {
    (scope || document).querySelectorAll('[data-odn-date-days]').forEach(function (el) {
      var days = parseInt(el.getAttribute('data-odn-date-days'), 10);
      if (isNaN(days) || days < 0) return;
      var text = formatDate(days);
      /* Only write when the text actually differs. Writing — even the same
         words — counts as a change to the drawer, which the observer below
         answers by running this again: a loop that repeated every frame,
         forever, and could freeze a heavy page. */
      if (el.textContent !== text) el.textContent = text;
    });
  }

  /* ======================================================================
     Delivery preference

     Only shown when the cart holds both ready-to-ship and made-to-order
     pieces and the store lets the customer choose. Saved as a cart
     attribute so it arrives on the order with the rest of the details.
     ====================================================================== */
  document.addEventListener('change', function (e) {
    var radio = e.target.closest ? e.target.closest('[data-odn-ship-pref]') : null;
    if (!radio || !radio.checked) return;

    /* Note the choice on the sync record first, so the automatic write
       below never mistakes it for something to correct. */
    markSaved('pref', radio.value);

    var body = { attributes: { 'Delivery preference': radio.value } };
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).catch(function () {
      /* The radio stays as chosen. A failed save only means the order
         carries the default, which is to send ready pieces first. */
    });
  });

  /* ======================================================================
     Ring size confirmation

     Stored as a cart attribute, so it travels to the order and you can
     see that the customer confirmed. It deliberately does not block
     checkout — it is a prompt, not a gate.
     ====================================================================== */
  function setAttribute(value) {
    var body = { attributes: {} };
    body.attributes[ATTRIBUTE] = value;

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  document.addEventListener('change', function (e) {
    var box = e.target.closest ? e.target.closest('[data-odn-size-check]') : null;
    if (!box) return;

    var wrap = box.closest('[data-odn-size]');
    if (wrap) wrap.setAttribute('data-saving', 'true');

    markSaved('ring', box.checked ? 'Yes' : '');

    setAttribute(box.checked ? 'Yes' : '')
      .catch(function () {
        /* Leave the box as the customer set it. The attribute is a
           record for us, not something worth interrupting them over. */
      })
      .then(function () {
        if (wrap) wrap.removeAttribute('data-saving');
      });
  });

  /* ======================================================================
     Pairing suggestion
     ====================================================================== */
  function loadPairing(scope) {
    (scope || document).querySelectorAll('[data-odn-pair]').forEach(function (holder) {
      if (holder.dataset.loaded === '1') return;
      holder.dataset.loaded = '1';

      var id = holder.dataset.productId;
      var slot = holder.querySelector('[data-odn-pair-slot]');
      if (!id || !slot) return;

      var url =
        holder.dataset.url +
        '?section_id=odins-cart-pairing' +
        '&product_id=' + encodeURIComponent(id) +
        '&intent=complementary&limit=1';

      fetch(url)
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          var card = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-odn-pair-card]');

          /* Nothing set in Search & Discovery for this product — leave
             the block hidden rather than showing an empty heading. */
          if (!card) return;

          /* Do not suggest something already in the cart. */
          if (inCart(card)) return;

          slot.appendChild(document.importNode(card, true));
          holder.hidden = false;
        })
        .catch(function () { /* stays hidden */ });
    });
  }

  function inCart(card) {
    var btn = card.querySelector('[data-odn-pair-add]');
    if (!btn) return false;
    var id = btn.dataset.variantId;
    return !!document.querySelector(
      '.cart-item [name="updates[]"][data-quantity-variant-id="' + id + '"]'
    );
  }

  /* Quick add from the pairing row. Only rendered for products with a
     single variant and no configure tag, so nothing with options can be
     added without going to its page first. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-odn-pair-add]') : null;
    if (!btn || btn.dataset.state === 'busy') return;

    e.preventDefault();
    btn.dataset.state = 'busy';

    var drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    var toRender = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender() : null;
    var body = { items: [{ id: Number(btn.dataset.variantId), quantity: 1 }] };

    if (toRender) {
      body.sections = toRender.map(function (x) { return x.id; });
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (json.status) throw new Error(json.description || json.message);
        if (drawer && drawer.renderContents) {
          drawer.renderContents(json);
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        btn.dataset.state = '';
        btn.textContent = btn.dataset.label;
      });
  });

  /* ======================================================================
     Re-run after every drawer refresh

     Dawn replaces the drawer's innerHTML on quantity change, removal and
     add to cart. The markup comes back but nothing is initialised, so an
     observer re-runs the parts that need script.
     ====================================================================== */
  /* ======================================================================
     Keep the order's record correct

     The cart says what Delivery preference and Ring size verified ought
     to be, beside what they currently are. Where they differ, the right
     value is written — so an order carries a delivery preference even
     when the customer left the default alone, and a cart that no longer
     holds a ring stops claiming its size was checked.

     Each drawer refresh renders a fresh record from the server, so this
     settles after one write rather than repeating.
     ====================================================================== */
  function markSaved(which, value) {
    var rec = document.querySelector('[data-odn-sync]');
    if (!rec) return;
    rec.setAttribute('data-' + which + '-saved', value);
    rec.setAttribute('data-' + which + '-desired', value);
  }

  function syncAttributes(scope) {
    var rec = (scope || document).querySelector('[data-odn-sync]');
    if (!rec || rec.dataset.syncing === '1') return;

    var changes = {};
    var any = false;

    if (rec.dataset.prefDesired !== rec.dataset.prefSaved) {
      changes['Delivery preference'] = rec.dataset.prefDesired;
      any = true;
    }
    if (rec.dataset.ringDesired !== rec.dataset.ringSaved) {
      changes[ATTRIBUTE] = rec.dataset.ringDesired;
      any = true;
    }
    if (!any) return;

    rec.dataset.syncing = '1';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      /* An empty value removes the attribute from the cart entirely. */
      body: JSON.stringify({ attributes: changes })
    })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        rec.dataset.prefSaved = rec.dataset.prefDesired;
        rec.dataset.ringSaved = rec.dataset.ringDesired;
      })
      .catch(function () { /* tried again on the next refresh */ })
      .then(function () { delete rec.dataset.syncing; });
  }

  /* ======================================================================
     Refresh after a Razorpay Buy Now

     Razorpay Magic Checkout's Buy Now replaces whatever is in the bag with
     the one piece being bought, then opens its checkout over the page. It
     changes the bag on Shopify's side only; nothing tells the drawer, so
     a customer who closes the checkout and opens their bag would still see
     the old pieces, until a reload.

     So when a Buy Now button is tapped — on a product page or in the
     collection's quick add sheet — the drawer is quietly redrawn from
     Shopify a moment later, without opening it. Twice, because how long
     Razorpay takes varies; a redraw that finds nothing changed is harmless.

     Razorpay claims these clicks at the document and stops them there, so
     this listens at the window, one step earlier. It only notes the tap;
     it never touches the click itself.
     ====================================================================== */
  var BUY_NOW = [
    '.rzp-magic-btn__buy-product',
    'magic-checkout-btn[page-type="product"]',
    'button.shopify-payment-button__button--unbranded'
  ].join(', ');

  var refreshTimers = [];

  function refreshDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.getSectionsToRender || !drawer.getSectionInnerHTML) return;
    var sections = drawer.getSectionsToRender();
    var ids = sections.map(function (s) { return s.id; }).join(',');

    fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids))
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (json) {
        sections.forEach(function (s) {
          var html = json[s.id];
          var el = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
          if (!html || !el) return;
          /* Each section on its own, so one that fails to redraw cannot
             stop the others — the bag count still updates if the drawer
             could not, and the other way round. */
          try {
            el.innerHTML = drawer.getSectionInnerHTML(html, s.selector);
          } catch (err) {
            return;
          }

          /* The drawer's empty state lives on the drawer element itself,
             outside the part that is redrawn, so it is set from Shopify's
             answer too — otherwise a bag that went from empty to one piece
             would stay hidden behind the empty message. */
          if (s.id === 'cart-drawer') {
            var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
            if (fresh) drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
          }
        });
      })
      .catch(function () { /* the next reload shows the right bag anyway */ });
  }

  window.addEventListener('click', function (e) {
    var hit = e.target && e.target.closest ? e.target.closest(BUY_NOW) : null;
    if (!hit) return;
    refreshTimers.forEach(window.clearTimeout);
    refreshTimers = [window.setTimeout(refreshDrawer, 2500), window.setTimeout(refreshDrawer, 6000)];
  }, true);

  function init(scope) {
    paintDates(scope);
    loadPairing(scope);
    syncAttributes(scope);
  }

  function watch() {
    var target = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');
    if (!target) return;

    var frame = null;
    new MutationObserver(function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        init(target);
      });
    }).observe(target, { childList: true, subtree: true });
  }

  init(document);
  watch();

  /* The drawer may not exist yet on a cached first paint. */
  document.addEventListener('DOMContentLoaded', function () {
    init(document);
    watch();
  });
})();